import React, { useState, useRef, useEffect } from "react";
import { json } from "react-router-dom";

const MobileSlider = ({ groupedCarouselData, autoSlideInterval = 4000, defaultBanner }) => {
  const [currentIndex, setCurrentIndex] = useState(groupedCarouselData.length || 0);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [isResetting, setIsResetting] = useState(false);
  const sliderRef = useRef(null);
  const autoSlideRef = useRef(null);
  const resetTimeoutRef = useRef(null);

  // Extend slides for infinite effect
  // Initial extended list
  let extendedData = [
    ...groupedCarouselData,
    ...groupedCarouselData,
    ...groupedCarouselData
  ];

  useEffect(() => {
    setCurrentIndex(groupedCarouselData.length); // Start from the first original slide
  }, [groupedCarouselData]);

  useEffect(() => {
    const n = groupedCarouselData.length;
    if (!n) return;

    // Clear any existing timeout first
    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current);
    }

    // Forward: reached end copy
    if (currentIndex >= 2 * n) {
      setIsResetting(true);
      resetTimeoutRef.current = setTimeout(() => {
        setIsTransitioning(false);
        setCurrentIndex(currentIndex - n); // jump back to real slides
        requestAnimationFrame(() => {
          setIsTransitioning(true);
          setIsResetting(false);
        });
      }, 500);
    }

    // Backward: reached start copy
    if (currentIndex < n) {
      setIsResetting(true);
      resetTimeoutRef.current = setTimeout(() => {
        setIsTransitioning(false);
        setCurrentIndex(currentIndex + n); // jump forward to real slides
        requestAnimationFrame(() => {
          setIsTransitioning(true);
          setIsResetting(false);
        });
      }, 500);
    }

    // Cleanup on unmount
    return () => clearTimeout(resetTimeoutRef.current);

  }, [currentIndex, groupedCarouselData.length]);

  // Swipe handling
  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    let startX = 0;
    let isDown = false;

    const handleTouchStart = (e) => {
      isDown = true;
      startX = e.touches[0].clientX;
    };

    const handleTouchMove = (e) => {
      if (!isDown || isResetting) return;
      const moveX = e.touches[0].clientX;
      const diff = startX - moveX;

      if (Math.abs(diff) > 50) {
        setIsTransitioning(true);
        if (diff > 0) {
          setCurrentIndex((prev) => prev + 1);
        } else {
          setCurrentIndex((prev) => prev - 1);
        }
        isDown = false;
        // **Reset auto-slide timer**
        handleUserInteraction();
      }
    };

    const handleTouchEnd = () => (isDown = false);

    slider.addEventListener("touchstart", handleTouchStart);
    slider.addEventListener("touchmove", handleTouchMove);
    slider.addEventListener("touchend", handleTouchEnd);

    return () => {
      slider.removeEventListener("touchstart", handleTouchStart);
      slider.removeEventListener("touchmove", handleTouchMove);
      slider.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isResetting]);

  // Auto-slide
  const handleUserInteraction = () => {
    if (autoSlideRef.current) clearInterval(autoSlideRef.current);
    startAutoSlide(); // restart the timer
  };

  const startAutoSlide = () => {
    if (autoSlideRef.current) clearInterval(autoSlideRef.current);
    autoSlideRef.current = setInterval(() => {
      if (isResetting) return;
      setCurrentIndex(prev => prev + 1);
      setIsTransitioning(true);
    }, autoSlideInterval);
  };

  useEffect(() => {
    startAutoSlide();
    return () => clearInterval(autoSlideRef.current);
  }, [autoSlideInterval, isResetting]);

  const handleDotClick = (index) => {
    setCurrentIndex(index);
  };

  return (
    <div className="mobile-slider-wrapper" ref={sliderRef}>
      <section className="section-hero">
        <div className="container">
          <div
            className="hero justify-content-start top-slider"
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              position: "relative",
              width: "100%",
            }}
          >
            {extendedData?.map((group, index) => (
              <div
                key={index}
                className="slide"
                style={{
                  display: "inline-block",
                  width: "100%",
                  transform: `translateX(-${currentIndex * 100}%)`,
                  transition: isTransitioning
                    ? "transform 0.5s ease-in-out"
                    : "none",
                }}
              >
                <img
                  className="banner_image_width"
                  src={`${process.env.REACT_APP_CLIENT_URL}${group[0]?.mobileImage}`}
                  alt="Banner"
                  style={{ width: "100%", borderRadius: "8px" }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = defaultBanner;
                  }}
                  onClick={() => {
                    window.location.href = group[0]?.link;
                  }}
                />
              </div>
            ))}
          </div>
          {groupedCarouselData?.length > 1 && (
            <div className="dots-container text-center">
              {groupedCarouselData?.map((_, index) => (
                <>
                  <span
                    key={index}
                    className={`dot1 ${(index + (groupedCarouselData?.length)) === currentIndex ? "active" : ""
                      }`}
                    onClick={() => handleDotClick((index + (groupedCarouselData?.length)))}
                  >
                  </span >
                </>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default MobileSlider;
